import React from 'react';
import Data from 'data';
import Locale from 'data/locale';
import { formatDescription } from 'utils/format';
import { getIconPath } from 'common';
import classNames from 'classnames';

const _L = Locale("bnet-tooltips.js");

function fixTooltip(tip) {
  return tip.replace(/\/webgl\/icons\/(\d+)/, (m, id) => getIconPath(id));
}

function SkillTooltip({ charClass, skill, rune }) {
  const info = Data.skills[charClass][skill];
  const tip = Data.skilltips[charClass][skill];
  const element = (tip.elements ? tip.elements[rune] : "phy");

  return (
    <div className="d3-tooltip d3-tooltip-skill">
      <div className={`tooltip-skill-effect effect-${element}`}>
        <div className="tooltip-head"><h3>{info.name}</h3></div>
      </div>
      <div dangerouslySetInnerHTML={{__html: fixTooltip(tip.x)}}/>
      {rune !== "x" && (
        <div className="tooltip-extension rune-extension">
          <span className="d3-icon d3-icon-rune d3-icon-rune-large"><span className={`rune-${rune}`}/></span>
          <div dangerouslySetInnerHTML={{__html: `<h3 class="header-3">${info.runes[rune]}</h3>` + tip[rune]}}/>
        </div>
      )}
    </div>
  );
}

export const FollowerSkillTooltip = React.memo(function FollowerSkillTooltip({ id }) {
  const info = Data.followerSkills[id];
  const tip = `<p>${info.description}</p><p class="subtle">${_L`Unlocked at level ${`<em>${info.level}</em>`}.`}</p>`;
  return (
    <div className="d3-tooltip d3-tooltip-skill">
      <div className="tooltip-head"><h3>{info.name}</h3></div>
      <div className="tooltip-body">
        <span className="d3-icon d3-icon-skill d3-icon-skill-64" style={{backgroundImage: fixTooltip(`url(/webgl/icons/${info.icon})`), width: 64, height: 64}}>
          <span className="frame"/>
        </span>
        <div className="description" dangerouslySetInnerHTML={{__html: tip}}/>
      </div>
    </div>
  );
});

export const BuffTooltip = React.memo(function BuffTooltip({ id }) {
  const info = Data.shrineBuffs[id];
  return (
    <div className="d3-tooltip d3-tooltip-skill">
      <div className="tooltip-head"><h3>{info.name}</h3></div>
      <div className="tooltip-body">
        <span className="d3-icon d3-icon-skill d3-icon-skill-64" style={{backgroundImage: fixTooltip(`url(/webgl/icons/${info.icon})`), width: 64, height: 64, backgroundSize: "64px auto"}}>
          <span className="frame"/>
        </span>
        <div className="description">
          {info.description.map((desc, idx) => <p key={idx}>{formatDescription(desc)}</p>)}
        </div>
      </div>
    </div>
  );
});

function RuneTooltip({ charClass, skill, rune }) {
  const info = Data.skills[charClass][skill];
  const tip = Data.skilltips[charClass][skill];
  const element = (tip.elements ? tip.elements[rune] : "phy");

  let desc = tip[rune];
  const pos = desc.indexOf("<p class=\"subtle\">");
  if (pos >= 0) {
    desc = desc.slice(0, pos) + `<p class="special">${info.name}</p>` + desc.slice(pos);
  }

  return (
    <div className="d3-tooltip d3-tooltip-rune">
      <div className={`tooltip-skill-effect effect-${element}`}>
        <div className="tooltip-head"><h3>{info.runes[rune]}</h3></div>
      </div>
      <div className="tooltip-body">
        <span className="d3-icon d3-icon-rune d3-icon-rune-medium"><span className={`rune-${rune}`}/></span>
        <div className="description" dangerouslySetInnerHTML={{__html: desc}}/>
        <span className="clear"/>
      </div>
    </div>
  );
}

function PassiveTooltip({ charClass, passive }) {
  const info = Data.passives[charClass][passive];
  const tip = Data.passivetips[charClass][passive];

  return (
    <div className="d3-tooltip d3-tooltip-trait">
      <div className="tooltip-head"><h3>{info.name}</h3></div>
      <div dangerouslySetInnerHTML={{__html: fixTooltip(tip)}}/>
    </div>
  );
}

function AttackTooltip({ skill }) {
  const info = Data.skills.attack[skill];

  return (
    <div className="d3-tooltip d3-tooltip-skill">
      <div className="tooltip-skill-effect effect-phy">
        <div className="tooltip-head"><h3>{info.name}</h3></div>
      </div>
      <div className="tooltip-body">
        <span className={`d3-icon d3-icon-skill d3-icon-skill-64 d3p-class-attack d3p-skill-icon d3p-${info.id}`}>
          <span className="frame"/>
        </span>
        <div className="description">
          <p dangerouslySetInnerHTML={{__html: info.tip}}/>
        </div>
      </div>
    </div>
  );
}

export default React.memo(function SkillTooltipPicker({ charClass, skill, rune, onlyRune }) {
  if (charClass === "attack" && Data.skills.attack[skill]) {
    return <AttackTooltip skill={skill}/>;
  } else if (Data.skilltips[charClass] && Data.skilltips[charClass][skill]) {
    if (!onlyRune || !rune || rune === "x") {
      return <SkillTooltip charClass={charClass} skill={skill} rune={rune || "x"}/>;
    } else {
      return <RuneTooltip charClass={charClass} skill={skill} rune={rune}/>;
    }
  } else if (Data.passivetips[charClass] && Data.passivetips[charClass][skill]) {
    return <PassiveTooltip charClass={charClass} passive={skill}/>;
  }
  return null;
});


export const AltarTooltip = React.memo(function AltarTooltip({ id }) {
  const info = Data.altar[id];
  return (
    <div className="d3-tooltip d3-tooltip-skill">
      <div className="tooltip-head"><h3>{info.name}</h3></div>
      <div className="tooltip-body">
        <span className={classNames("d3-icon d3-icon-skill d3-icon-skill-64 d3p-altar-icon", {"d3p-altar-major": info.major || info.final})}
            style={{backgroundPositionX: `-${info.icon + (info.final ? 0.5 : 0)}em`}}>
          <span className="frame"/>
        </span>
        <div className="description">
          {info.desc}
        </div>
      </div>
      <div className="tooltip-extension">
        <div className="flavor">{info.flavor}</div>
      </div>
    </div>
  );
});
